import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMe } from "../../auth/queries";
import { signOut } from "../../auth/guard";
import { DownloadAppButton } from "../../features/download/DownloadAppButton";
import { useDesktopDownload } from "../../features/download/queries";
import { paths } from "../../lib/endpoints";
import { useI18n } from "../../i18n/I18nProvider";
import { BoardsBand } from "../rink/BoardsBand";
import { Container } from "./Container";
import { FaceoffDot } from "../rink/FaceoffDot";
import { Menu } from "../ui/Menu";
import { LanguageToggle } from "./LanguageToggle";
import { MobileAccountMenu } from "./MobileAccountMenu";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { useShellWorkspace } from "./useShellWorkspace";

export interface HeaderProps {
  workspaceId?: string | undefined;
}

const ADMIN_LINK =
  "inline-flex min-h-11 items-center px-2 font-display text-lg font-bold uppercase tracking-[0.01em] text-ink-2 hover:text-ink";

/** Dasher-board band: wordmark with a red faceoff dot, switcher, admin link, language and the account menu. */
export function Header({ workspaceId }: HeaderProps) {
  const { t } = useI18n();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const shellWorkspace = useShellWorkspace(workspaceId);
  const operator = me.data ?? null;
  const showSwitcher = !shellWorkspace.isError;
  const desktop = useDesktopDownload().data;

  const onSignOut = async () => {
    await signOut(queryClient);
    await navigate({ to: "/login" });
  };

  return (
    <BoardsBand>
      <Container className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Link
          to={operator ? "/app" : "/login"}
          className="inline-flex min-h-11 items-center gap-2 font-display text-2xl leading-none font-extrabold tracking-[0.01em] text-ink uppercase"
        >
          <FaceoffDot tone="red" />
          {t("app.name")}
        </Link>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {operator && desktop ? (
            <div className="hidden sm:block">
              <DownloadAppButton variant="compact" />
            </div>
          ) : null}
          {operator && showSwitcher ? (
            <div className="hidden sm:block">
              <WorkspaceSwitcher currentId={workspaceId} />
            </div>
          ) : null}
          {operator?.isAdmin ? (
            <Link to="/admin" className={`${ADMIN_LINK} hidden sm:inline-flex`}>
              {t("admin.nav")}
            </Link>
          ) : null}
          <div className="hidden sm:block">
            <LanguageToggle />
          </div>
          {operator ? (
            <div className="flex items-center gap-1">
              <span className="hidden max-w-[12rem] truncate text-note text-ink-2 lg:inline">
                {operator.email}
              </span>
              <div className="hidden sm:block">
                <Menu
                  label={t("user.menu")}
                  items={[
                    ...(desktop
                      ? [
                          {
                            label: t("download.compact"),
                            onSelect: () => window.location.assign(paths.desktopDownload),
                          },
                        ]
                      : []),
                    { label: t("user.signOut"), onSelect: () => void onSignOut() },
                  ]}
                />
              </div>
              <div className="sm:hidden">
                <MobileAccountMenu
                  workspaceId={workspaceId}
                  showWorkspaces={showSwitcher}
                  isAdmin={operator.isAdmin}
                  desktop={Boolean(desktop)}
                  onSignOut={() => void onSignOut()}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Container>
    </BoardsBand>
  );
}
